import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MessageSquare, History, Plus, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Layout } from '@/components/wiki/Layout';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TalkPage() {
  const { slug } = useParams();
  const { user, getAuthHeader } = useAuth();
  const [page, setPage] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTopic, setShowNewTopic] = useState(false);
  const [newTopic, setNewTopic] = useState('');
  const [newContent, setNewContent] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const pageResponse = await axios.get(`${API}/pages/${slug}`);
        setPage(pageResponse.data);
        
        const talkResponse = await axios.get(`${API}/talk/${pageResponse.data.id}`);
        setPosts(talkResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  const handleNewTopic = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please log in to post');
      return;
    }
    if (!newTopic.trim() || !newContent.trim()) {
      toast.error('Topic and content are required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/talk`, {
        page_id: page.id,
        topic: newTopic.trim(),
        content: newContent.trim()
      }, { headers: getAuthHeader() });
      
      setPosts([response.data, ...posts]);
      setNewTopic('');
      setNewContent('');
      setShowNewTopic(false);
      toast.success('Topic created!');
    } catch (error) {
      toast.error('Failed to create topic');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (postId, topic) => {
    if (!user) {
      toast.error('Please log in to reply');
      return;
    }
    if (!replyContent.trim()) {
      toast.error('Reply content is required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/talk`, {
        page_id: page.id,
        topic,
        content: replyContent.trim(),
        parent_id: postId
      }, { headers: getAuthHeader() });
      
      setPosts(posts.map(post => {
        if (post.id === postId) {
          return { ...post, replies: [...(post.replies || []), response.data] };
        }
        return post;
      }));
      
      setReplyContent('');
      setReplyingTo(null);
      toast.success('Reply posted!');
    } catch (error) {
      toast.error('Failed to post reply');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-10 bg-[#242424] rounded w-1/3" />
            <div className="h-32 bg-[#242424] rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Page Header */}
      <div className="bg-[#0F0F0F] border-b border-[#333333]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <Tabs defaultValue="talk" className="w-auto">
              <TabsList className="bg-[#242424]">
                <TabsTrigger 
                  value="article" 
                  onClick={() => window.location.href = `/wiki/${slug}`}
                  className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#1A1A1A] text-gray-300"
                >
                  Article
                </TabsTrigger>
                <TabsTrigger value="talk" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#1A1A1A] text-gray-300" data-testid="talk-tab-active">
                  <MessageSquare className="h-4 w-4 mr-1" />
                  Talk
                </TabsTrigger>
                <TabsTrigger 
                  value="history" 
                  onClick={() => window.location.href = `/wiki/${slug}/history`}
                  className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#1A1A1A] text-gray-300"
                >
                  <History className="h-4 w-4 mr-1" />
                  History
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="talk-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to={`/wiki/${slug}`} className="text-sm text-[#D4AF37] hover:underline flex items-center gap-1 mb-2">
              <ArrowLeft className="h-4 w-4" />
              Back to {page?.title}
            </Link>
            <h1 className="font-spectral text-2xl font-bold text-white">
              Talk: {page?.title}
            </h1>
          </div>
          {user && (
            <Button 
              onClick={() => setShowNewTopic(!showNewTopic)}
              className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#1A1A1A]"
              data-testid="new-topic-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Topic
            </Button>
          )}
        </div>

        {/* New Topic Form */}
        {showNewTopic && (
          <form onSubmit={handleNewTopic} className="bg-[#242424] rounded-xl border border-[#333333] p-6 mb-6 animate-fadeIn" data-testid="new-topic-form">
            <h3 className="font-semibold text-white mb-4">Start a New Discussion</h3>
            <div className="space-y-4">
              <Input
                placeholder="Topic title..."
                value={newTopic}
                onChange={(e) => setNewTopic(e.target.value)}
                className="bg-[#1A1A1A] border-[#333333] text-white placeholder:text-gray-400"
                data-testid="topic-title-input"
              />
              <Textarea
                placeholder="What would you like to discuss?"
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                rows={4}
                className="bg-[#1A1A1A] border-[#333333] text-white placeholder:text-gray-400"
                data-testid="topic-content-input"
              />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowNewTopic(false)} className="border-[#333333] text-gray-300 hover:bg-[#333333]">
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#1A1A1A]" data-testid="submit-topic-btn">
                  <Send className="h-4 w-4 mr-2" />
                  {submitting ? 'Posting...' : 'Post Topic'}
                </Button>
              </div>
            </div>
          </form>
        )}

        {/* Discussion Threads */}
        <div className="space-y-6">
          {posts.length === 0 ? (
            <div className="bg-[#242424] rounded-xl border border-[#333333] p-12 text-center">
              <MessageSquare className="h-16 w-16 mx-auto mb-4 text-gray-400" />
              <h3 className="font-spectral text-xl font-semibold text-white mb-2">No discussions yet</h3>
              <p className="text-gray-400 mb-6">Be the first to start a conversation about this article!</p>
              {user ? (
                <Button onClick={() => setShowNewTopic(true)} className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#1A1A1A]">
                  Start a Discussion
                </Button>
              ) : (
                <Link to="/login">
                  <Button className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#1A1A1A]">Log in to Discuss</Button>
                </Link>
              )}
            </div>
          ) : (
            posts.map((post) => (
              <div key={post.id} className="bg-[#242424] rounded-xl border border-[#333333] overflow-hidden" data-testid={`talk-post-${post.id}`}>
                <div className="bg-[#1A1A1A] px-6 py-4 border-b border-[#333333]">
                  <h3 className="font-semibold text-white">{post.topic}</h3>
                </div>
                
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center text-[#1A1A1A] text-sm font-medium flex-shrink-0">
                      {post.author_username[0].toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-white">{post.author_username}</span>
                        <span className="text-sm text-gray-400">
                          {new Date(post.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-gray-300 whitespace-pre-wrap">{post.content}</p>
                    </div>
                  </div>

                  {post.replies && post.replies.length > 0 && (
                    <div className="mt-6 ml-14 space-y-4 border-l-2 border-[#333333] pl-4">
                      {post.replies.map((reply) => (
                        <div key={reply.id} className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#333333] flex items-center justify-center text-gray-300 text-xs font-medium flex-shrink-0">
                            {reply.author_username[0].toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm text-white">{reply.author_username}</span>
                              <span className="text-xs text-gray-400">
                                {new Date(reply.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-gray-300 text-sm whitespace-pre-wrap">{reply.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {replyingTo === post.id ? (
                    <div className="mt-4 ml-14">
                      <Textarea
                        placeholder="Write your reply..."
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        rows={3}
                        className="mb-2 bg-[#1A1A1A] border-[#333333] text-white placeholder:text-gray-400"
                        data-testid={`reply-input-${post.id}`}
                      />
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => { setReplyingTo(null); setReplyContent(''); }} className="border-[#333333] text-gray-300">
                          Cancel
                        </Button>
                        <Button 
                          size="sm" 
                          onClick={() => handleReply(post.id, post.topic)}
                          disabled={submitting}
                          className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#1A1A1A]"
                          data-testid={`submit-reply-${post.id}`}
                        >
                          {submitting ? 'Posting...' : 'Reply'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    user && (
                      <div className="mt-4 ml-14">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setReplyingTo(post.id)}
                          className="text-[#D4AF37] hover:bg-[#333333]"
                          data-testid={`reply-btn-${post.id}`}
                        >
                          <MessageSquare className="h-4 w-4 mr-1" />
                          Reply
                        </Button>
                      </div>
                    )
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}
